import { computed, defineComponent, h, onMounted, ref, shallowRef, watchEffect } from "vue";
import type { PropType, Ref } from "vue";
import { coreCreateEditor } from "@wangeditor/core";
import type { IDomEditor } from "@wangeditor/core";
import { CHANGE_EVENT, UPDATE_MODEL_EVENT } from "element-plus";
import plugin from "./plugin";
import paragraphPlugin from "./paragraph/plugin";
import Popper from "./Popper";
import { getPrefixCls } from "@vue-admin-core/utils/const";
import emitter from "./mitt";
import { nodeToText, textToHtml, textToNode } from "./utils";
import { isEmpty } from "lodash-es";
import { onClickOutside } from "@vueuse/core";

const prefixCls = getPrefixCls("msg-editor");

export default defineComponent({
  name: "VacMsgEditor",
  props: {
    /**
     * 编辑器内容
     */
    modelValue: {
      type: String,
      default: ""
    },
    /**
     * 变量下拉选项数据
     */
    options: {
      type: Array as PropType<{ label: string; value: string }[]>,
      default: () => []
    },
    /**
     * 占位符
     */
    placeholder: {
      type: String
    },
    /** 是否禁用 */
    disabled: {
      type: Boolean
    },
    readOnly: {
      type: Boolean
    },
    /**
     * 变量前缀
     */
    prefix: {
      type: String,
      default: "\\$\\{"
    },
    /**
     * 变量后缀
     */
    suffix: {
      type: String,
      default: "\\}"
    }
  },
  emits: [
    CHANGE_EVENT,
    UPDATE_MODEL_EVENT,
    /**
     * 创建完成事件
     * @params editor
     */
    "created",
    /**
     * 销毁事件
     * @params editor
     */
    "destroyed",
    "maxLength",
    /**
     * 获取焦点
     * @params editor
     */
    "focus",
    /**
     * 失去焦点
     * @params editor
     */
    "blur",
    "customAlert",
    /**
     * 自定义粘贴
     * @params editor, event
     * @return res
     */
    "customPaste"
  ],
  setup(props, context) {
    const selector = ref();
    const visible = ref(false);
    const editorRef = shallowRef<null | IDomEditor>(null); // editor 实例，必须用 shallowRef
    const curValue = ref(""); // 记录 editor 当前 html 内容
    const position = ref();
    const editor = ref();
    const popper = ref<{ setValue: (item: any) => void; target: Ref<HTMLDivElement> }>();
    const selectValue = ref();
    let isCreated = false;

    const optionMap = computed(() =>
      props.options.reduce((acc, item) => ({ ...acc, [item.value]: item }), {})
    );

    const initEditor = () => {
      if (!selector.value) return;
      editor.value = coreCreateEditor({
        selector: selector.value,
        config: {
          placeholder: props.placeholder,
          readOnly: props.disabled || props.readOnly,
          onCreated(editor) {
            editorRef.value = editor; // 记录 editor 实例
            setTimeout(() => (isCreated = true));
          },
          onChange(editor) {
            const editorHtml = editor.getHtml();
            curValue.value = editorHtml; // 记录当前内容
            if (isCreated) {
              context.emit(
                "update:modelValue",
                nodeToText(editor.children, { prefix: props.prefix, suffix: props.suffix })
              ); // 触发 v-model 值变化
              context.emit(
                "change",
                nodeToText(editor.children, { prefix: props.prefix, suffix: props.suffix }),
                editor
              );
            }
          },
          onDestroyed(editor) {
            context.emit("destroyed", editor);
          },
          onMaxLength(editor) {
            context.emit("maxLength", editor);
          },
          onFocus(editor) {
            context.emit("focus", editor);
          },
          onBlur(editor) {
            // emitter.emit("hide", editor);
            context.emit("blur", editor);
          },
          customAlert(info, type) {
            context.emit("customAlert", info, type);
          },
          customPaste: (editor, event): any => {
            const clipboardData = event.clipboardData;
            const pastedText = clipboardData?.getData("text");
            editor.insertNode(
              textToNode(
                { prefix: props.prefix, suffix: props.suffix },
                pastedText,
                optionMap.value
              ) as any
            );
            return false;
          }
        },
        html: textToHtml(props.modelValue, optionMap.value, {
          prefix: props.prefix,
          suffix: props.suffix
        }), //curValue.value,
        plugins: [paragraphPlugin, plugin]
      });
      emitter.on("show", ({ value, ..._position }) => {
        position.value = _position;
        visible.value = true;
        selectValue.value = value;
        // @ts-ignore
        if (value) popper.value?.setValue(optionMap.value[value]);
      });
      emitter.on("hide", () => {
        visible.value = false;
      });
      onClickOutside(selector.value, (event) => {
        if (!popper.value?.target.value?.contains(event.target as HTMLElement)) {
          visible.value = false;
        }
      });
    };

    watchEffect(() => {
      if (!editor.value) return;
      if (props.disabled || props.readOnly) {
        emitter.emit("hide", editor.value);
        editor.value.disable();
      } else {
        editor.value.enable();
      }
    });

    /**
     * 设置 HTML
     * @param newHtml new html
     */
    function setHtml(newHtml: string) {
      const editor = editorRef.value;
      if (editor == null) return;
      editor.setHtml(newHtml);
    }

    /**
     * 元素挂在后初始化编辑器
     */
    onMounted(() => {
      initEditor();
    });

    /**
     * 监听 v-model 值变化
     */
    watchEffect(() => {
      if (isEmpty(optionMap.value)) return;
      const _val = textToHtml(props.modelValue, optionMap.value, {
        prefix: props.prefix,
        suffix: props.suffix
      });
      if (_val === curValue.value) return; // 和当前内容一样，则忽略
      // 重新设置 HTML
      setHtml(_val);
    });
    return () =>
      h(
        "div",
        {
          ...context.attrs,
          class: [prefixCls, "el-textarea", context.attrs.class]
        },
        {
          default: () => [
            h("div", {
              ref: selector,
              class: [`${prefixCls}__content`, { "is-disabled": props.disabled }],
              onKeydown: (e: KeyboardEvent) => {
                if (e.key === "ArrowDown" || e.key === "ArrowUp" || e.key === "Enter") {
                  if (visible.value) {
                    e.preventDefault();
                    emitter.emit(e.key, e);
                  }
                }
              }
            }),
            h(Popper, {
              ref: popper,
              class: `${prefixCls}__popper`,
              visible: visible.value,
              position: position,
              editor: editor.value,
              value: selectValue.value,
              options: props.options
            })
          ]
        }
      );
  }
});
